/**
 * Media Card Component
 *
 * Displays songs, albums, artists, playlists.
 */

import { Link } from 'react-router-dom';
import { PlayIcon, MoreVertIcon } from '../icons';

interface Thumbnail {
  url: string;
  width: number;
  height: number;
}

interface MediaItem {
  id: string;
  title?: string;
  name?: string;
  thumbnails?: Thumbnail[];
  thumbnail?: string;
  artists?: Array<{ name: string }>;
  album?: { name: string };
  duration?: number;
  year?: string;
  subtitle?: string;
}

interface MediaCardProps {
  item: MediaItem;
  variant: 'grid' | 'list';
  isArtist?: boolean;
  onClick?: () => void;
}

export function MediaCard({ item, variant, isArtist, onClick }: MediaCardProps) {
  const thumbnail = item.thumbnail || item.thumbnails?.[0]?.url || './pwa-192x192.webp';
  const title = item.title || item.name || 'Unknown';
  const subtitle = item.artists?.map((a) => a.name).join(', ') || item.subtitle || item.year || '';

  const linkTo = isArtist
    ? `/artist/${item.id}`
    : item.id.startsWith('MPREb_') || item.id.startsWith('OLAK')
      ? `/album/${item.id}`
      : item.id.startsWith('VL') || item.id.startsWith('PL')
        ? `/playlist/${item.id}`
        : undefined;

  const content = variant === 'grid' ? (
    <div className="group relative cursor-pointer">
      <div className={`relative aspect-square mb-3 overflow-hidden bg-surface-container ${isArtist ? 'rounded-full' : 'rounded-xl'}`}>
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          loading="lazy"
          onError={(e) => {
            // Show fallback on error
            (e.target as HTMLImageElement).src = './pwa-192x192.webp';
          }}
        />

        {/* Play button overlay */}
        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center
          opacity-0 group-hover:opacity-100 transition-opacity duration-200
          ${isArtist ? 'rounded-full' : 'rounded-xl'}`}
        >
          <button
            className="w-12 h-12 rounded-full bg-primary flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick?.();
            }}
          >
            <PlayIcon className="w-5 h-5 ml-0.5 text-on-primary" />
          </button>
        </div>
      </div>

      <p className="font-medium line-clamp-1">{title}</p>
      {subtitle && (
        <p className="text-sm text-on-surface-variant line-clamp-1">{subtitle}</p>
      )}
    </div>
  ) : (
    <div className="list-item group" onClick={onClick}>
      <img
        src={thumbnail}
        alt={title}
        className={`w-12 h-12 object-cover shrink-0 ${isArtist ? 'rounded-full' : 'rounded-lg'}`}
        referrerPolicy="no-referrer"
        crossOrigin="anonymous"
        loading="lazy"
        onError={(e) => {
          (e.target as HTMLImageElement).src = './pwa-192x192.webp';
        }}
      />

      <div className="flex-1 min-w-0">
        <p className="font-medium line-clamp-1">{title}</p>
        {subtitle && (
          <p className="text-sm text-on-surface-variant line-clamp-1">{subtitle}</p>
        )}
      </div>

      {item.duration && (
        <span className="text-sm text-on-surface-variant shrink-0 tabular-nums">
          {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
        </span>
      )}

      <button
        className="btn-icon opacity-0 group-hover:opacity-100 shrink-0"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <MoreVertIcon className="w-5 h-5" />
      </button>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo} className="block">{content}</Link>;
  }

  return content;
}
